import { Component, ViewChild, ElementRef, AfterViewChecked, TemplateRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChatService } from '../../services/chat.service';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { marked } from 'marked';
import { SortPipe } from '../../sort.pipe';
import { SearchPipe } from '../../search.pipe';
import { Suggestion } from 'src/app/models/suggestion';
import {
    Subject,
    debounceTime,
    distinctUntilChanged,
    switchMap,
    filter
} from 'rxjs';
import * as XLSX from 'xlsx';
import Chart from 'chart.js/auto';

interface ChatMessage {
  id: string;
  question: string;
  answer: string;
  formattedAnswer?: SafeHtml;
  isLoading?: boolean;
  tableData?: any[];
  tableHeaders?: string[];
  sortKey?: string;
  sortDirection?: string;
  searchQuery?: string;
}

@Component({
  selector: 'app-admin-chat',
  imports: [CommonModule, FormsModule, SortPipe, SearchPipe],
  templateUrl: './admin-chat.component.html',
  styleUrls: ['./admin-chat.component.css']
})
export class AdminChatComponent implements OnInit ,AfterViewChecked {
  @ViewChild('chatContainer') chatContainer: ElementRef;
  @ViewChild('confirmDeleteTemplate') confirmDeleteTemplate: TemplateRef<any>;
  @ViewChild('messageInput') messageInput!: ElementRef;
  @ViewChild('graphModalTemplate') graphModalTemplate!: TemplateRef<any>;
  modalRef!: MatDialogRef<any>;
  graphModalRef!: MatDialogRef<any>;
  
  currentGraphMessage?: ChatMessage;
  selectedGraphColumn: string = '';
  selectedGraphType: string = 'bar';
  chartInstance: any;
  suggestions: Suggestion[] = [];
  showSuggestions = false;
  selectedIndex = -1;
  private searchSubject = new Subject<string>();
  messages: ChatMessage[] = [];
  newMessage: string = '';
  private shouldScroll = false;

  constructor(
    private chatService: ChatService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog
  ) { }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  ngOnInit(): void {
    this.searchSubject.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      filter(query => query.trim().length > 0),
      switchMap( query => this.chatService.getSuggestion(query) )
    ).subscribe({
      next: (res) =>{
        if (!this.newMessage.trim()) {
          this.suggestions = [];
          this.showSuggestions = false;
          this.selectedIndex = -1;
          return;
        }
        this.suggestions = res;
        this.showSuggestions = res.length > 0
        this.selectedIndex = -1
      },
      error: ()=>{
        this.suggestions = [];
        this.showSuggestions = false;
      }
    })
  }

  onInput(): void{
    this.adjustTextareaHeight();
    const value = this.newMessage.trim()
    if (!value){
      this.showSuggestions = false
      this.suggestions = []
      return
    }
    this.searchSubject.next(value)
  }

  handleKeyDown(event: KeyboardEvent): void {
    if (this.showSuggestions) {
        switch (event.key) {

            case 'ArrowDown':

                event.preventDefault();

                this.selectedIndex = Math.min(
                    this.selectedIndex + 1,
                    this.suggestions.length - 1
                );

                return;

            case 'ArrowUp':

                event.preventDefault();

                this.selectedIndex = Math.max(
                    this.selectedIndex - 1,
                    0
                );

                return;

            case 'Enter':

                if (this.selectedIndex >= 0) {

                    event.preventDefault();

                    this.selectSuggestion(
                        this.suggestions[this.selectedIndex]
                    );

                    return;

                }

        }

    }

    if (event.key === 'Enter' && !event.shiftKey) {

        event.preventDefault();

        this.sendMessage();

    }

  }

  selectSuggestion(suggestion: Suggestion): void {
    this.newMessage = suggestion.completion;
    this.showSuggestions = false;
    this.suggestions = [];
    setTimeout(() => this.adjustTextareaHeight(), 0);
  }

  useQuickSuggestion(text: string): void {
    this.newMessage = text;
    this.sendMessage();
  }

  adjustTextareaHeight(): void {
    if (this.messageInput) {
      const textarea = this.messageInput.nativeElement;
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }

  resetTextareaHeight(): void {
    if (this.messageInput) {
      const textarea = this.messageInput.nativeElement;
      textarea.style.height = 'auto';
    }
  }

  /**
   * Format the answer and extract markdown tables into dynamic objects
   */
  async formatAnswer(message: ChatMessage, answer: string): Promise<SafeHtml> {
    if (!answer) return '';

    // Configure marked to use gfm (GitHub Flavored Markdown)
    marked.setOptions({
      gfm: true,
      breaks: true
    });

    const tokens = marked.lexer(answer);
    let htmlContent = '';

    message.tableData = undefined;
    message.tableHeaders = undefined;

    for (const token of tokens) {
      if (token.type === 'table') {
        const headers = token.header.map(h => h.text);
        const tableData = token.rows.map(row => {
          const rowData: any = {};
          headers.forEach((h, index) => {
            rowData[h] = row[index].text;
          });
          return rowData;
        });

        message.tableHeaders = headers;
        message.tableData = tableData;
        message.sortKey = headers[0];
        message.sortDirection = 'asc';
        message.searchQuery = '';
      } else {
        htmlContent += await marked.parse(token.raw);
      }
    }

    // Sanitize and return as SafeHtml
    return this.sanitizer.sanitize(1, htmlContent) || '';
  }

  sort(message: ChatMessage, header: string): void {
    if (message.sortKey === header) {
      message.sortDirection = message.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      message.sortKey = header;
      message.sortDirection = 'asc';
    }
  }

  sendMessage(): void {
    if (!this.newMessage.trim()) return;

    this.showSuggestions = false;
    this.suggestions = [];
    this.selectedIndex = -1;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      question: this.newMessage,
      answer: '',
      isLoading: true
    };

    this.messages.push(userMessage);
    this.shouldScroll = true;

    const messageToSend = this.newMessage;
    this.newMessage = '';
    this.resetTextareaHeight();

    this.chatService.askQuestion(messageToSend).subscribe({
      next: (response) => {
        const index = this.messages.findIndex(m => m.id === userMessage.id);
        if (index !== -1) {
          let answerText = '';
          if (response.answer) {
            answerText = response.answer;
          } else if (response.response) {
            answerText = response.response;
          } else if (typeof response === 'string') {
            answerText = response;
          } else {
            answerText = 'No response received';
          }

          this.messages[index].answer = answerText;
          this.formatAnswer(this.messages[index], answerText).then(formatted => {
            this.messages[index].formattedAnswer = formatted;
            this.messages[index].isLoading = false;
            this.shouldScroll = true;
          });
        }
      },
      error: (err) => {
        console.error('Chat Error:', err);
        const index = this.messages.findIndex(m => m.id === userMessage.id);
        if (index !== -1) {
          let errorMessage = 'Failed to get response. ';
          if (err.status === 0) {
            errorMessage += 'Unable to connect to server.';
          } else if (err.status === 404) {
            errorMessage += 'API endpoint not found.';
          } else if (err.status === 500) {
            errorMessage += 'Server error occurred.';
          } else if (err.error?.message) {
            errorMessage += err.error.message;
          } else {
            errorMessage += 'Please try again.';
          }

          this.messages[index].answer = errorMessage;
          this.formatAnswer(this.messages[index], errorMessage).then(formatted => {
            this.messages[index].formattedAnswer = formatted;
            this.messages[index].isLoading = false;
            this.shouldScroll = true;
          });
        }
      }
    });
  }

  handleKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  scrollToBottom(): void {
    try {
      if (this.chatContainer) {
        this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
      }
    } catch (err) {
      console.error('Scroll error:', err);
    }
  }

  clearChat(): void {
    this.modalRef = this.dialog.open(this.confirmDeleteTemplate, {
      width: '400px'
    });
  }

  confirmClear(): void {
    this.messages = [];
    this.modalRef.close();
  }

  cancelClear(): void {
    this.modalRef.close();
  }

  exportToExcel(message: ChatMessage): void {
    if (!message.tableData || message.tableData.length === 0) return;
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(message.tableData, {header: message.tableHeaders});
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, 'export.xlsx');
  }

  openGraphModal(message: ChatMessage): void {
    if (!message.tableData || message.tableData.length === 0) return;
    this.currentGraphMessage = message;
    
    // Pick the second column by default if available, otherwise first
    this.selectedGraphColumn = message.tableHeaders && message.tableHeaders.length > 1 ? message.tableHeaders[1] : (message.tableHeaders ? message.tableHeaders[0] : '');
    this.selectedGraphType = 'bar';
    
    this.graphModalRef = this.dialog.open(this.graphModalTemplate, {
      width: '700px',
      maxWidth: '90vw',
      panelClass: 'graph-modal-panel'
    });
    
    this.graphModalRef.afterOpened().subscribe(() => {
      setTimeout(() => this.updateGraph(), 100);
    });
  }

  closeGraphModal(): void {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
    this.graphModalRef.close();
  }

  updateGraph(): void {
    if (!this.currentGraphMessage || !this.currentGraphMessage.tableData) return;
    
    const data = this.currentGraphMessage.tableData;
    const col = this.selectedGraphColumn;
    
    const counts: { [key: string]: number } = {};
    for (const row of data) {
      const val = row[col] ? String(row[col]) : 'Unknown';
      counts[val] = (counts[val] || 0) + 1;
    }
    
    const labels = Object.keys(counts);
    const values = Object.values(counts);
    
    const canvas = document.getElementById('dynamicChartCanvas') as HTMLCanvasElement;
    if (!canvas) return;
    
    if (this.chartInstance) {
      this.chartInstance.destroy();
    }
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    this.chartInstance = new Chart(ctx, {
      type: this.selectedGraphType as any,
      data: {
        labels: labels,
        datasets: [{
          label: 'Record Count',
          data: values,
          backgroundColor: [
            '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
            '#06b6d4', '#f97316', '#14b8a6', '#64748b', '#ec4899'
          ],
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: this.selectedGraphType !== 'bar'
          }
        },
        scales: (this.selectedGraphType === 'bar' || this.selectedGraphType === 'line') ? {
          y: {
            beginAtZero: true,
            ticks: {
                stepSize: 1
            }
          }
        } : undefined
      }
    });
  }
}